import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import API_URL from '../config';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';


const PerformanceJournal = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [pastDates, setPastDates] = useState([]);
  const [showingPastEntries, setShowingPastEntries] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    event: '',
    preparation: '',
    mentalState: '',
    performance: '',
    strengths: '',
    improvements: '',
    nextSteps: '',
    performanceRating: 5,
    mentalRating: 5
  });

  useEffect(() => {
    // Track component view
    Mixpanel.trackFeatureAction('Journal', 'View Performance Journal');
    
    // Check if there's a journal entry for today
    checkEntryForDate();
  }, []);

  // Check if there's a journal entry for this date
  const checkEntryForDate = async (specificDate = null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const date = specificDate || formData.date;
      const response = await axios.get(`${API_URL}/api/performance-journal/${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.entry) {
        // Map backend data structure to frontend structure
        const entry = response.data.entry;
        setFormData({
          date: entry.date ? new Date(entry.date).toISOString().split('T')[0] : date,
          event: entry.event || '',
          preparation: entry.preparation || '',
          mentalState: entry.mentalState || '',
          performance: entry.performance || '',
          strengths: entry.strengths || '',
          improvements: entry.improvements || '',
          nextSteps: entry.nextSteps || '',
          performanceRating: entry.performanceRating || 5,
          mentalRating: entry.mentalRating || 5
        });
      }
    } catch (error) {
      logger.error('Error fetching performance journal entry:', error);
      // No need to show error to user if there's no entry yet
    }
  };

  // Fetch all past journal entries
  const fetchPastJournalEntries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      
      // Get all journal dates for the performance type
      const response = await axios.get(`${API_URL}/api/journal/dates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { type: 'performance' }
      });
      
      if (response.data && Array.isArray(response.data)) {
        // Sort dates in descending order (newest first)
        const sortedDates = response.data.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        
        setPastDates(sortedDates);
        setShowingPastEntries(true);
      }
    } catch (error) {
      logger.error('Error fetching past journal entries:', error);
      setError('Failed to load past journal entries. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If date is changing, reset the form and check for entries on the new date
    if (name === 'date' && value !== formData.date) {
      setFormData({
        date: value,
        event: '',
        preparation: '',
        mentalState: '',
        performance: '',
        strengths: '',
        improvements: '',
        nextSteps: '',
        performanceRating: 5,
        mentalRating: 5
      });
      
      // Check if there's an entry for the new date
      setTimeout(() => {
        checkEntryForDate(value);
      }, 100);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSaved(false);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to save a journal entry');
        return;
      }
      
      // Prepare data for backend
      const backendData = {
        date: formData.date,
        event: formData.event,
        preparation: formData.preparation,
        mentalState: formData.mentalState,
        performance: formData.performance,
        strengths: formData.strengths,
        improvements: formData.improvements,
        nextSteps: formData.nextSteps,
        performanceRating: formData.performanceRating,
        mentalRating: formData.mentalRating,
        type: 'performance'
      };
      
      await axios.post(`${API_URL}/api/journal`, backendData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSaved(true);
      
      // Track successful journal entry
      Mixpanel.trackFeatureAction('Journal', 'Performance Journal Saved', {
        date: formData.date,
        performanceRating: formData.performanceRating,
        mentalRating: formData.mentalRating
      });
      
    } catch (error) {
      logger.error('Error saving journal entry:', error);
      setError('Failed to save your journal entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleNewEntry = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      event: '',
      preparation: '',
      mentalState: '',
      performance: '',
      strengths: '',
      improvements: '',
      nextSteps: '',
      performanceRating: 5,
      mentalRating: 5
    });
    setSaved(false);
    setShowingPastEntries(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">Performance Journal</h2>
      
      {error && (
        <div className="alert alert-error mb-4">
          <span>{error}</span>
        </div>
      )}
      
      {saved ? (
        <div className="text-center py-8">
          <div className="alert alert-success mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span>Your performance journal entry has been saved successfully!</span>
          </div>
          <button 
            onClick={handleNewEntry}
            className="btn btn-primary"
          >
            Create New Entry
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={fetchPastJournalEntries}
              className="btn btn-secondary btn-sm"
            >
              {showingPastEntries ? "Hide Past Entries" : "Review Past Journals"}
            </button>
            
            {showingPastEntries && (
              <button
                type="button"
                onClick={handleNewEntry}
                className="btn btn-outline btn-sm"
              >
                New Entry
              </button>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
              Date
            </label>
            {showingPastEntries ? (
              <select
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="select select-bordered w-full"
                required
              >
                <option value="" disabled>Select a date</option>
                {pastDates.map(entry => (
                  <option key={entry._id || entry.date} value={entry.date}>
                    {new Date(entry.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="input input-bordered w-full"
                required
              />
            )}
          </div>
          
          <div className="flex flex-col gap-4 mb-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="event">
                Event/Competition
              </label>
              <input
                type="text"
                id="event"
                name="event"
                value={formData.event}
                onChange={handleInputChange}
                className="input input-bordered w-full"
                placeholder="What event or competition are you reflecting on?"
                required
              />
            </div>
            
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="performanceRating">
                Performance Rating (1-10)
              </label>
              <input
                type="range"
                id="performanceRating"
                name="performanceRating"
                min="1"
                max="10"
                value={formData.performanceRating}
                onChange={handleInputChange}
                className="range range-primary w-full"
                style={{ width: '100%' }}
              />
              <div className="flex justify-between text-xs mt-1">
                <span>1</span>
                <span>5</span>
                <span>10</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-4 mb-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mentalRating">
                Mental Performance Rating (1-10)
              </label>
              <input
                type="range"
                id="mentalRating"
                name="mentalRating"
                min="1"
                max="10"
                value={formData.mentalRating}
                onChange={handleInputChange}
                className="range range-primary w-full"
                style={{ width: '100%' }}
              />
              <div className="flex justify-between text-xs mt-1">
                <span>1</span>
                <span>5</span>
                <span>10</span>
              </div>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="preparation">
              Preparation
            </label>
            <textarea
              id="preparation"
              name="preparation"
              value={formData.preparation}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-20"
              placeholder="How did you prepare for this event? What was your routine?"
              required
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="mentalState">
              Mental State
            </label>
            <textarea
              id="mentalState"
              name="mentalState"
              value={formData.mentalState}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-20"
              placeholder="How were you feeling mentally before and during the event?"
              required
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="performance">
              Performance Summary
            </label>
            <textarea
              id="performance"
              name="performance"
              value={formData.performance}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-20"
              placeholder="Describe your overall performance and key moments"
              required
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="strengths">
              Strengths Demonstrated
            </label>
            <textarea
              id="strengths"
              name="strengths"
              value={formData.strengths}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-20"
              placeholder="What mental and physical strengths did you demonstrate?"
              required
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="improvements">
              Areas for Improvement
            </label>
            <textarea
              id="improvements"
              name="improvements"
              value={formData.improvements}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-20"
              placeholder="What could you improve for next time?"
              required
            ></textarea>
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="nextSteps">
              Next Steps
            </label>
            <textarea
              id="nextSteps"
              name="nextSteps"
              value={formData.nextSteps}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full h-20"
              placeholder="What specific actions will you take to improve?"
              required
            ></textarea>
          </div>
          
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  Saving...
                </>
              ) : 'Save Performance Journal'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PerformanceJournal;